import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import {
  Gauge, ListNumbers, PencilLine, UsersThree, Warning, FileText,
  SealCheck, Gear, ClockCounterClockwise, MagnifyingGlass, Export as ExportIcon,
  Play, Pause, ArrowLeft, SignOut, ArrowCounterClockwise, List as ListIcon, X as XIcon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { projectApi, analysisApi } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Brand, ThemeToggle } from "@/components/Brand";
import SearchDialog from "@/components/SearchDialog";
import ExportMenu from "@/components/ExportMenu";
import Dashboard from "@/pages/Dashboard";
import Structure from "@/pages/Structure";
import Memory from "@/pages/Memory";
import Consistency from "@/pages/Consistency";
import Report from "@/pages/Report";
import Settings from "@/pages/Settings";
import Changelog from "@/pages/Changelog";

const ProjectContext = createContext(null);
export const useProject = () => useContext(ProjectContext);

const NAV = [
  { to: "", label: "Tableau de bord", icon: Gauge, end: true },
  { to: "structure", label: "Structure", icon: ListNumbers },
  { to: "editor", label: "Éditeur", icon: PencilLine, external: true },
  { to: "memory", label: "Mémoire du roman", icon: UsersThree },
  { to: "consistency", label: "Cohérence", icon: Warning },
  { to: "report", label: "Rapport", icon: FileText },
  { to: "finalcheck", label: "Contrôle final", icon: SealCheck },
  { to: "changelog", label: "Journal", icon: ClockCounterClockwise },
  { to: "settings", label: "Paramètres", icon: Gear },
];

export default function ProjectShell() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState({ status: "idle", done: 0, total: 0, percent: 0 });
  const [searchOpen, setSearchOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);

  const loadProject = useCallback(() => {
    projectApi.get(pid).then(setProject).catch(() => navigate("/"));
  }, [pid, navigate]);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await analysisApi.status(pid);
      setStatus(s);
      return s;
    } catch (e) { return null; }
  }, [pid]);

  useEffect(() => { loadProject(); refreshStatus(); }, [loadProject, refreshStatus]);

  useEffect(() => {
    if (status.status === "running") {
      pollRef.current = setInterval(refreshStatus, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [status.status, refreshStatus]);

  useEffect(() => {
    if (status.status === "running" && status.started_at) {
      const t = setInterval(() => {
        setElapsed(Math.floor((Date.now() - new Date(status.started_at).getTime()) / 1000));
      }, 1000);
      return () => clearInterval(t);
    }
  }, [status.status, status.started_at]);

  const startAnalysis = async () => {
    const s = await analysisApi.start(pid);
    setStatus(s);
    toast.success("Analyse lancée");
  };
  const pauseAnalysis = async () => {
    const s = await analysisApi.pause(pid);
    setStatus(s);
    toast.info("Analyse interrompue");
  };
  const restartAnalysis = async () => {
    if (!window.confirm("Relancer l'analyse depuis le début ? Tous les chapitres seront réanalysés (vos corrections déjà acceptées/refusées sont conservées).")) return;
    const s = await analysisApi.restart(pid);
    setStatus(s);
    toast.success("Analyse relancée depuis le début");
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (!project) return <div className="min-h-screen grid place-items-center text-muted-foreground">Chargement…</div>;

  const running = status.status === "running";
  const paused = status.status === "paused" && status.done < status.total;

  return (
    <ProjectContext.Provider value={{ project, loadProject, status, refreshStatus, startAnalysis, pauseAnalysis, restartAnalysis }}>
      <div className="min-h-screen bg-background flex">
        {/* Mobile backdrop */}
        {navOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setNavOpen(false)} data-testid="nav-backdrop" />
        )}
        {/* Sidebar */}
        <aside className={`${navOpen ? "flex" : "hidden"} lg:flex w-72 lg:w-64 flex-shrink-0 border-r border-border bg-surface h-screen fixed lg:sticky top-0 left-0 z-50 flex-col`}>
          <div className="p-5 border-b border-border flex items-center justify-between">
            <Brand onClick={() => navigate("/")} size={22} />
            <button className="lg:hidden text-muted-foreground" onClick={() => setNavOpen(false)} data-testid="close-nav"><XIcon size={20} /></button>
          </div>
          <div className="p-4 border-b border-border">
            <button onClick={() => navigate("/")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" data-testid="back-to-projects">
              <ArrowLeft size={14} /> Tous les projets
            </button>
            <p className="overline text-muted-foreground">{project.genre}</p>
            <h2 className="font-serif text-lg leading-tight mt-1 line-clamp-2">{project.name}</h2>
            <p className="text-xs text-muted-foreground font-mono mt-2">{project.chapter_count} chapitres · {(project.word_count || 0).toLocaleString("fr")} mots</p>
          </div>
          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto thin-scroll" onClick={() => setNavOpen(false)}>
            {NAV.map((n) => {
              const Icon = n.icon;
              if (n.external) {
                return (
                  <button key={n.to} data-testid={`nav-${n.to}`} onClick={() => navigate(`/project/${pid}/editor`)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors">
                    <Icon size={18} weight="duotone" /> {n.label}
                  </button>
                );
              }
              return (
                <NavLink key={n.to} to={n.to ? `/project/${pid}/${n.to}` : `/project/${pid}`} end={n.to === ""} data-testid={`nav-${n.to || "dashboard"}`}
                  className={({ isActive }) =>
                    `w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                      isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-surface-hover hover:text-foreground"
                    }`
                  }>
                  <Icon size={18} weight="duotone" /> {n.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="p-3 border-t border-border flex items-center gap-2">
            <ThemeToggle />
            <button onClick={logout} className="h-9 flex-1 grid place-items-center rounded-sm border border-border hover:bg-surface-hover text-sm gap-2 flex items-center justify-center" data-testid="shell-logout">
              <SignOut size={16} /> Quitter
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <header className="min-h-16 border-b border-border bg-surface/80 backdrop-blur sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 gap-2 sm:gap-4 py-2 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button className="lg:hidden h-9 w-9 grid place-items-center rounded-sm border border-border flex-shrink-0" onClick={() => setNavOpen(true)} data-testid="open-nav">
                <ListIcon size={18} />
              </button>
              <button onClick={() => setSearchOpen(true)} data-testid="open-search" className="flex items-center gap-2 text-sm text-muted-foreground border border-border rounded-sm px-3 h-9 hover:bg-surface-hover transition-colors flex-1 min-w-0 sm:min-w-[220px] sm:flex-none">
                <MagnifyingGlass size={16} className="flex-shrink-0" /> <span className="truncate">Rechercher…</span>
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {running ? (
                <Button variant="outline" size="sm" onClick={pauseAnalysis} data-testid="pause-analysis" className="rounded-sm gap-2">
                  <Pause size={16} weight="fill" /> <span className="hidden sm:inline">Interrompre</span>
                </Button>
              ) : (
                <Button size="sm" onClick={startAnalysis} data-testid="start-analysis" className="rounded-sm gap-2">
                  <Play size={16} weight="fill" /> <span className="hidden sm:inline">{paused ? "Reprendre l'analyse" : "Analyser le manuscrit"}</span><span className="sm:hidden">{paused ? "Reprendre" : "Analyser"}</span>
                </Button>
              )}
              {!running && status.total > 0 && (
                <Button variant="ghost" size="sm" onClick={restartAnalysis} data-testid="restart-analysis" className="rounded-sm gap-2" title="Réanalyser tout le manuscrit depuis le chapitre 1">
                  <ArrowCounterClockwise size={16} /> <span className="hidden sm:inline">Recommencer</span>
                </Button>
              )}
              <ExportMenu pid={pid} />
            </div>
          </header>

          {/* Analysis banner */}
          {(running || paused) && (
            <div className="px-6 py-3 border-b border-border bg-primary/5 flex items-center gap-4 fade-up" data-testid="analysis-banner">
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium">
                    {running ? `Analyse en cours — Chapitre ${status.current_chapter ?? "…"} / ${status.total}` : `Analyse interrompue — ${status.done} / ${status.total} chapitres terminés`}
                    {running && status.current_title ? ` · ${status.current_title}` : ""}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {running && `${fmtTime(elapsed)} · `}{status.problems_found || 0} problèmes détectés
                  </span>
                </div>
                <Progress value={status.percent} className="h-1.5" />
              </div>
            </div>
          )}

          <main className="flex-1 overflow-y-auto thin-scroll">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="structure" element={<Structure />} />
              <Route path="memory" element={<Memory />} />
              <Route path="consistency" element={<Consistency />} />
              <Route path="report" element={<Report />} />
              <Route path="finalcheck" element={<Report finalCheck />} />
              <Route path="changelog" element={<Changelog />} />
              <Route path="settings" element={<Settings />} />
              <Route path="*" element={<Navigate to={`/project/${pid}`} replace />} />
            </Routes>
          </main>
        </div>
      </div>
      <SearchDialog pid={pid} open={searchOpen} onOpenChange={setSearchOpen} />
    </ProjectContext.Provider>
  );
}
